import CryptoJS from "crypto-js";
import { STORAGE_KEYS } from "../constants/app-config";

const SECRET = process.env.REACT_APP_SECURE_LOCAL_STORAGE_SECRET_KEY ?? "";
const BROWSER_SCOPE = "browser";

const decrypt = (encrypted: string | null): string | null => {
  if (!encrypted) return null;
  try {
    const decrypted = CryptoJS.AES.decrypt(encrypted, SECRET).toString(
      CryptoJS.enc.Utf8,
    );
    return decrypted || null;
  } catch {
    console.error("Error decrypting local storage item");
    return null;
  }
};

const secureLocalStorage = {
  setItem: (key: string, value: string) => {
    // sessions live only in localStorage; a stray copy in sessionStorage must never outlive this write
    sessionStorage.removeItem(key);
    localStorage.setItem(key, CryptoJS.AES.encrypt(value, SECRET).toString());
  },
  getItem: (key: string): string | null => decrypt(localStorage.getItem(key)),
  setJSON: <T>(key: string, value: T) => {
    secureLocalStorage.setItem(key, JSON.stringify(value));
  },
  getJSON: <T>(key: string): T | null => {
    const raw = secureLocalStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      console.error("Failed to parse JSON from storage", key);
      secureLocalStorage.removeItem(key);
      return null;
    }
  },
  // sessionStorage too, so a stray copy cannot survive a sign-out
  removeItem: (key: string) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  },
  clear: () => {
    localStorage.clear();
    // not sessionStorage.clear(): other modules keep caches there, and at least one documents that it outlives a sign-out
    sessionStorage.removeItem(STORAGE_KEYS.USER);
  },
};

const isSecureOrigin = () => window.location.protocol === "https:";

// __Host- stops a parent-domain cookie of the same name shadowing this one; it demands Secure, so http cannot use it.
const cookieName = () =>
  isSecureOrigin() ? `__Host-${STORAGE_KEYS.BROWSER_SESSION_COOKIE}` : STORAGE_KEYS.BROWSER_SESSION_COOKIE;

const cookieAttributes = () =>
  `path=/; SameSite=Lax${isSecureOrigin() ? "; Secure" : ""}`;

const hasBrowserSessionMarker = () =>
  document.cookie.split(";").some((entry) => {
    const [name, ...value] = entry.trim().split("=");
    return name === cookieName() && value.join("=") === "1";
  });

export const scopeSessionToBrowser = () => {
  // no Max-Age and no Expires: the browser drops it at quit, the one lifetime web storage cannot express
  document.cookie = `${cookieName()}=1; ${cookieAttributes()}`;
  // cookies refused: stay remembered, or the session is swept at the next page load instead of at quit
  if (!hasBrowserSessionMarker()) return;
  // plain, not encrypted: a failed decrypt returns null, which would read as "remembered"
  localStorage.setItem(STORAGE_KEYS.SESSION_SCOPE, BROWSER_SCOPE);
};

export const clearSessionScope = () => {
  localStorage.removeItem(STORAGE_KEYS.SESSION_SCOPE);
  document.cookie = `${cookieName()}=; Max-Age=0; ${cookieAttributes()}`;
};

// A named-key sweep, not localStorage.clear(): other modules' settings on this origin outlive a sign-out.
export const clearSessionKeys = () => {
  clearSessionScope();
  secureLocalStorage.removeItem(STORAGE_KEYS.USER);
  secureLocalStorage.removeItem(STORAGE_KEYS.ORG);
  // ลบ Key แจ้งเตือน เพื่อให้คนถัดไปที่มา Login ได้รับการแจ้งเตือนใหม่
  localStorage.removeItem(STORAGE_KEYS.LAST_NOTI_CHECK);
  // ล้างค่า Filter ของ Eco-Efficiency Dashboard ทิ้งทั้งหมด
  // ป้องกันคนล็อกอินเข้ามาใหม่แล้วเห็นข้อมูลของคนเก่า
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(STORAGE_KEYS.ECO_EFF_FILTER_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
};

// Read only to decide whether to send it; the server verifies the token itself.
const sessionIdOf = (accessToken: string): string | null => {
  try {
    const payload = JSON.parse(atob(accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload?.sid === "string" && payload.sid ? payload.sid : null;
  } catch {
    return null;
  }
};

// Without this the swept session's row keeps one of the account's session slots until it is evicted.
const endSweptSessionOnServer = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const accessToken = secureLocalStorage.getJSON<{ accessToken?: string }>(STORAGE_KEYS.USER)?.accessToken;
  // a token without a sid would sign out every device on the account
  if (!apiUrl || !accessToken || !sessionIdOf(accessToken)) return;
  try {
    // plain fetch: this runs before render, ahead of the axios interceptors and their redirects
    fetch(`${apiUrl}/v1/users/logout`, {
      method: "POST",
      keepalive: true,
      headers: { Authorization: `Bearer ${accessToken}` },
    }).catch(() => undefined);
  } catch {
    // best effort: the sweep below happens whether or not the server hears about it
  }
};

// Must run before the first reader: the scoped session shares localStorage with every tab, and only the marker dates it.
export const enforceBrowserSession = () => {
  const legacyUnticked = secureLocalStorage.getItem(STORAGE_KEYS.LEGACY_REMEMBER_ME) === "false";
  localStorage.removeItem(STORAGE_KEYS.LEGACY_REMEMBER_ME);
  // nothing marks when that session began, so the only safe reading of "don't remember me" is to end it once
  if (legacyUnticked && localStorage.getItem(STORAGE_KEYS.SESSION_SCOPE) === null) {
    endSweptSessionOnServer();
    clearSessionKeys();
    return;
  }
  if (localStorage.getItem(STORAGE_KEYS.SESSION_SCOPE) !== BROWSER_SCOPE) return;
  if (hasBrowserSessionMarker()) return;
  endSweptSessionOnServer();
  clearSessionKeys();
};

export default secureLocalStorage;
